/**
 * Employee profile location (users.id).
 *
 * @param {number|string|null|undefined} userId
 * @param {Record<string, string|number|boolean|null|undefined>} [query]
 * @returns {{ name: string, params: { id: string }, query: Record<string, unknown> } | null}
 */
export function employeeProfileLocation(userId, query = {}) {
  const id = Number(userId);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    name: 'employee-detail',
    params: { id: String(id) },
    query,
  };
}

/**
 * Cmd/Ctrl/Shift or middle-click — open profile in a new tab.
 *
 * @param {MouseEvent|null|undefined} event
 */
export function shouldOpenEmployeeProfileInNewTab(event) {
  if (!event || typeof event !== 'object') return false;
  if (typeof event.metaKey !== 'boolean' && typeof event.ctrlKey !== 'boolean') return false;
  return Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);
}

function openProfileHref(router, location) {
  try {
    const resolved = router.resolve(location);
    window.open(resolved.href, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(`/employees/${location.params.id}`, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Open employee profile (users.id) in the current SPA window.
 * Pass `{ newTab: true }` when leaving the page would lose a draft overlay.
 * Pass the click `event` so Cmd/Ctrl/Shift-click can still open a new tab.
 *
 * @param {import('vue-router').Router} router
 * @param {number|string|null|undefined} userId
 * @param {Record<string, string|number|boolean|null|undefined>} [query]
 * @param {{ newTab?: boolean, event?: MouseEvent|null }} [options]
 */
export function openEmployeeProfile(router, userId, query = {}, options = {}) {
  const location = employeeProfileLocation(userId, query);
  if (!location || !router) return;

  const openNewTab = options.newTab === true || shouldOpenEmployeeProfileInNewTab(options.event);
  if (openNewTab) {
    if (options.event && typeof options.event.preventDefault === 'function') {
      options.event.preventDefault();
    }
    openProfileHref(router, location);
    return;
  }

  return router.push(location).catch(() => {});
}
